/**
 * SavePrimaryButton (BLU-1376, handover 4.6): the one primary action on a step.
 *
 * idle (its label) -> saving (spinner, "Saving", disabled) -> saved (green, a
 * tick drawn in, "Saved"). The CALLER holds "saved" for `SETUP_SAVED_HOLD_MS`
 * (700ms) before moving on, and skips the hold when `prefersReducedMotion()`
 * (from `@/lib/scroll-container`) is true.
 *
 * All three labels sit stacked in one grid cell so the button keeps the width
 * of its widest label and never jumps as the text changes; only the active one
 * is visible. The visible text lives in an `aria-live="polite"` span, so the
 * change is announced (DESIGN 16).
 *
 * Inside a complete step's footer, beside "Next", it takes the secondary
 * (outlined) look until a save starts (`pane-action.ts`).
 */
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PANE_SECONDARY, usePaneActionTone } from './pane-action';

export type SavePhase = 'idle' | 'saving' | 'saved';

interface SavePrimaryButtonProps {
  phase: SavePhase;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  type?: 'button' | 'submit';
  className?: string;
  /** Ids of text that explains why the button is disabled (the footer hint). */
  describedBy?: string;
}

const LAYER = 'col-start-1 row-start-1 inline-flex items-center justify-center gap-1.5';

export const SavePrimaryButton = forwardRef<HTMLButtonElement, SavePrimaryButtonProps>(function SavePrimaryButton(
  { phase, disabled = false, onClick, children, type = 'button', className, describedBy },
  ref,
) {
  const { t } = useTranslation('onboarding');
  const busy = phase !== 'idle';
  const secondary = usePaneActionTone() === 'secondary';

  return (
    <Button
      ref={ref}
      type={type}
      aria-describedby={describedBy}
      size="sm"
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={phase === 'saving' || undefined}
      data-phase={phase}
      data-tone={secondary ? 'secondary' : 'primary'}
      className={cn(
        'h-8 px-3 text-xs text-white disabled:opacity-100',
        'transition-colors duration-150 ease-out motion-reduce:transition-none',
        phase === 'saved'
          ? 'bg-success hover:bg-success'
          : secondary && phase === 'idle'
            ? PANE_SECONDARY
            : 'bg-primary-strong hover:bg-primary-strong/90',
        // Disabled before any save (not mid-sequence): the usual dimmed look.
        disabled && !busy && 'opacity-50',
        className,
      )}
    >
      <span className="grid">
        <span aria-hidden="true" className={cn(LAYER, phase !== 'idle' && 'invisible')}>
          {children}
        </span>
        <span aria-hidden="true" className={cn(LAYER, phase !== 'saving' && 'invisible')}>
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
          {t('save.saving')}
        </span>
        <span aria-hidden="true" className={cn(LAYER, phase !== 'saved' && 'invisible')}>
          {phase === 'saved' && <Check className="h-3.5 w-3.5 motion-safe:animate-check-enter" strokeWidth={3} />}
          {t('save.saved')}
        </span>
      </span>
      <span aria-live="polite" className="sr-only">
        {phase === 'saving' ? t('save.saving') : phase === 'saved' ? t('save.saved') : children}
      </span>
    </Button>
  );
});
